(function () {
  const KEY = "AIzaSyBIWR4UpcfIa-3cZ-pnNS7XcJOi5hHKBYk";
  const MAP_ID = "5d278bcd39e22421";
  const { gmaps_geolocation, range } = window.store_locator;

  let map;
  let current_iso_code;
  let dynamicWaitTimeInterval = null;

  // ── Constants ─────────────────────────────────────────────────────────────────

  const MAP_MAX_ZOOM = 15;
  const MARKER_Z_DEFAULT = 1;
  const MARKER_Z_EMPHASIS = 10;
  const MOBILE_MAX_WIDTH = "(max-width: 991.98px)";
  const DESKTOP_MAP_MQ = "(min-width: 991px)";

  function isDesktopMapLayout() {
    return window.matchMedia(DESKTOP_MAP_MQ).matches;
  }

  // ── Helpers ───────────────────────────────────────────────────────────────────

  function formatDistance(distanceKm, isoCode = current_iso_code) {
    return isoCode === 'CA'
      ? `${Math.round(distanceKm * 10) / 10} km`
      : `${Math.round(distanceKm * 10 * 0.62137119) / 10} mi`;
  }

  function isValidLatLng(obj) {
    return !!obj && typeof obj.lat === 'number' && typeof obj.lng === 'number';
  }

  /**
   * True when the click is on an interactive control (not plain card body).
   * Links, form controls, fav — not wrappers like `.location__actions` (wait time is not a control).
   * Keep in sync with embark-map-list.js.
   */
  function shouldIgnoreLocationCardBodyClick(target) {
    const el = target?.nodeType === 1 ? target : target?.parentElement;
    if (!el || !(el instanceof Element)) return true;
    return !!el.closest(
      "a, button, input, textarea, select, #fav-location-icon, [data-favorite-store], .fav-location"
    );
  }

  /**
   * Live wait UI only when the store is open and check-in is not disabled.
   */
  function storeEligibleForLiveWaitUi(store) {
    const open =
      store?.is_the_store_open === true || store?.is_the_store_open === "true";
    const disabled =
      store?.check_in_disable === true || store?.check_in_disable === "true";
    return open && !disabled;
  }

  // ── class Store ───────────────────────────────────────────────────────────────

  class Store {
    constructor(data = {}) {
      Object.assign(this, data);
      this.waitTime = null;
    }

    getMarkerSubtitle() {
      if (typeof StoreStatus === "undefined" || !storeEligibleForLiveWaitUi(this)) {
        return "View Details";
      }
      return StoreStatus.getDisplayTimeLabel({
        waitTimeData: this.waitTimeData,
        numericWaitTime: this.waitTime,
      });
    }

    updateMarkerSubtitle() {
      if (!this.marker?.content) return;
      const label = this.marker.content.querySelector(".marker-label p");
      if (label) label.textContent = this.getMarkerSubtitle();
    }

    getDistance(position) {
      if (!isValidLatLng(position)) return Infinity;

      const R = 6371;
      const toRad = (deg) => deg * (Math.PI / 180);
      const dLat = toRad(this.location.lat - position.lat);
      const dLng = toRad(this.location.lng - position.lng);
      const a =
        Math.sin(dLat / 2) ** 2 +
        Math.cos(toRad(position.lat)) *
        Math.cos(toRad(this.location.lat)) *
        Math.sin(dLng / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    }

    addMarker(mapInstance) {
      const wrapper = document.createElement("div");
      wrapper.className = "tg-marker";
      if (this.shortcuts_id) {
        wrapper.setAttribute("data-shortcut-id", String(this.shortcuts_id));
      }
      wrapper.innerHTML = `
        <div class="marker-label">
          ${this.name}
          <p>${this.getMarkerSubtitle()}</p>
        </div>
      `;

      this.marker = new google.maps.marker.AdvancedMarkerElement({
        map: mapInstance,
        position: this.location,
        content: wrapper,
        title: this.name,
        zIndex: MARKER_Z_DEFAULT,
        gmpClickable: true,
      });
    }
  }

  // ── class Listing ─────────────────────────────────────────────────────────────

  class Listing {
    constructor(store, { element = null, showDistance = false } = {}) {
      this.store = store;
      this.showDistance = showDistance;

      if (element) {
        this.element = element;
        this.bindEvents();
      } else {
        this.createElement();
      }
    }

    destroy() {
      if (this.element) this.element.remove();
    }

    bindEvents() {
      if (!this.element) return;
      this.element.addEventListener("click", (event) => {
        if (shouldIgnoreLocationCardBodyClick(event.target)) return;

        if (window.matchMedia(MOBILE_MAX_WIDTH).matches && this.store.url) {
          window.location.href = this.store.url;
          return;
        }
        if (map?.focusStore) map.focusStore(this.store);
      });
    }

    createElement() {
      const existingCard = document.querySelector(
        `.location[data-shortcut-id="${this.store.shortcuts_id}"]`
      );
      if (!existingCard) {
        console.warn(`No existing card found for location: ${this.store.name}`);
        return;
      }

      const element = existingCard.cloneNode(true);

      const kmForDisplay = this.store.distanceFromDevice ?? this.store.distance;
      if (this.showDistance && typeof kmForDisplay === 'number' && Number.isFinite(kmForDisplay)) {
        const span = document.createElement('span');
        span.classList.add('location__distance');
        span.textContent = formatDistance(kmForDisplay);
        const title = element.querySelector('.location__title');
        if (title) title.insertAdjacentElement('afterend', span);
      }

      this.element = element;
      this.bindEvents();
    }
  }

  // ── class StoreLocator ────────────────────────────────────────────────────────

  class StoreLocator {
    constructor(container) {
      this.events = {};
      this.elements = {
        map: container,
        addressInput: document.querySelector('.js-map-search-input'),
        clearBtn: document.querySelector('.js-map-search-clear'),
        searchBtn: document.querySelector('.js-map-search-submit'),
        nearestStoresList: document.querySelector(".js-nearest-stores"),
        storesList: document.querySelectorAll('.js-stores-list .location'),
        nearbyContainer: document.querySelector(".locations-nearest"),
        staticLocations: document.querySelector(".locations-static"),
        locationFilters: document.querySelector('.location-filters'),
        loader: document.querySelector(".loader-wrapper"),
        noResults: document.querySelector(".js-no-results"),
        noResultsMessage: document.querySelector(".js-no-results-message"),
      };

      this._location = { lat: 50.885859584611325, lng: -89.17573073012652 };
      this.listings = [];
      this.listingsNearby = [];
      this.cachedGeocodes = {};
      this.currentSortMode = 'distance';
      this.cityFilter = null;
      this.defaultCenter = null;
      this._searchDebounceTimer = null;
      this._runSearch = null;
      this._cardHoverForMarker = null;

      this.getStores().then(() => this.emit('storesReady'));
      this.bindEvents();
    }

    // ── Events ────────────────────────────────────────────────────────────────

    on(name, cb) {
      (this.events[name] ||= []).push(cb);
    }

    emit(name, ...args) {
      (this.events[name] || []).forEach((cb) => cb(...args));
    }

    // ── Location getter / setter ──────────────────────────────────────────────

    set userLocation(location) {
      if (!isValidLatLng(location)) {
        console.warn('Invalid location provided:', location);
        return;
      }
      this._location = location;
      this.updateNearestStores();
    }

    get userLocation() {
      return this._location;
    }

    // ── Data loading ──────────────────────────────────────────────────────────

    async getStores() {
      try {
        if (!window.storeLocatorData) throw new Error('Store data not pre-loaded');

        const { iso_code, locations } = window.storeLocatorData;
        current_iso_code = iso_code;

        this.stores = locations
          .filter((s) => s.address && typeof s.address === "string" && s.shortcuts_id && s.location && s.country)
          .map((s) => new Store(s));

        const tryLoadMap = () => {
          if (!isDesktopMapLayout() || this.loaded) return;
          this.load();
        };

        if (isDesktopMapLayout()) tryLoadMap();

        const mq = window.matchMedia(DESKTOP_MAP_MQ);
        if (typeof mq.addEventListener === "function") {
          mq.addEventListener("change", tryLoadMap);
        } else {
          mq.addListener(tryLoadMap);
        }

        this.geolocate();
      } catch (error) {
        console.error("Failed to load stores:", error);
        this.showNoLocationsMessage();
      }
    }

    // ── Map lifecycle ─────────────────────────────────────────────────────────

    load() {
      this.loaded = true;
      this.map = new google.maps.Map(this.elements.map, {
        center: this.userLocation,
        mapId: MAP_ID,
        zoom: 14,
        maxZoom: MAP_MAX_ZOOM,
        clickableIcons: false,
      });

      this.setMarkers();

      if (this.geocoded || this.geolocated) {
        setTimeout(() => this.updateNearestStores(), 300);
      }
    }

    // ── Geolocation ───────────────────────────────────────────────────────────

    async geolocate() {
      this.cityFilter = null;

      if (!gmaps_geolocation) {
        this.showNoLocationsMessage();
        return;
      }

      if (this.cachedGeolocation) {
        this.userLocation = this.cachedGeolocation;
        return;
      }

      try {
        const response = await fetch(
          `https://www.googleapis.com/geolocation/v1/geolocate?key=${KEY}`,
          { method: "POST" }
        );
        if (!response.ok) throw new Error(`Geolocation API error: ${response.status}`);

        const { location } = await response.json();
        this.geolocated = true;
        this.cachedGeolocation = location;
        if (!this.defaultCenter) this.defaultCenter = { ...location };
        this.userLocation = location;
      } catch (error) {
        console.warn("Geolocation failed:", error);
        this.showNoLocationsMessage();
      }
    }

    // ── Search ────────────────────────────────────────────────────────────────

    getClosestStore(location) {
      if (!this.stores?.length) return null;
      let closest = null;
      let min = Infinity;
      for (const store of this.stores) {
        const d = store.getDistance(location);
        if (d < min) { min = d; closest = store; }
      }
      return { store: closest, distance: min };
    }

    applySmartSearch(location) {
      const closest = this.getClosestStore(location);
      if (closest && closest.distance <= range) {
        return { location: closest.store.location, foundNearbyStore: true };
      }
      return { location, foundNearbyStore: false };
    }

    applyLocationAndSmartSearch(location, cacheKey) {
      if (!isValidLatLng(location)) {
        console.warn('Invalid location provided to applyLocationAndSmartSearch:', location);
        return;
      }
      const result = this.applySmartSearch(location);
      this.noNearbyStoresFound = !result.foundNearbyStore;
      this.geocoded = true;
      this.userLocation = result.location;

      if (cacheKey && typeof cacheKey === 'string') {
        this.cachedGeocodes[cacheKey.toLowerCase()] = result.location;
      }
    }

    findLocalStoreMatch(query) {
      if (!this.stores?.length) return null;
      const q = query.toLowerCase();
      return this.stores.find((s) => {
        return s.name.toLowerCase().includes(q) || (s.city || '').toLowerCase().includes(q);
      }) || null;
    }

    initAutoSearch(inputElement) {
      if (!inputElement || !google.maps.places) return;

      this._runSearch = async (rawQuery) => {
        const query = (rawQuery || '').trim();
        if (!query || query.length < 2) return;

        const localMatch = this.findLocalStoreMatch(query);
        if (localMatch) {
          this.cityFilter = (localMatch.city || '').toLowerCase() || null;
          this.applyLocationAndSmartSearch(localMatch.location, localMatch.name);
          return;
        }

        this.cityFilter = null;

        const request = { input: query, includedRegionCodes: ['ca', 'us'] };
        if (isValidLatLng(this.userLocation)) {
          request.locationBias = this.userLocation;
        }

        try {
          const { suggestions } =
            await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(request);
          if (!suggestions?.length) return;

          const place = suggestions[0].placePrediction.toPlace();
          await place.fetchFields({ fields: ['location', 'displayName'] });

          if (place.location) {
            this.cityFilter = null;
            this.applyLocationAndSmartSearch(
              { lat: place.location.lat(), lng: place.location.lng() },
              place.displayName
            );
          }
        } catch (error) {
          console.warn('Places search failed:', error);
        }
      };

      inputElement.addEventListener('input', (e) => {
        clearTimeout(this._searchDebounceTimer);
        if (!e.target.value.trim()) { this.clearSearch(); return; }
        this._searchDebounceTimer = setTimeout(() => this._runSearch(e.target.value), 700);
        this.updateClearVisibility();
      });

      inputElement.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          clearTimeout(this._searchDebounceTimer);
          this._runSearch(e.target.value);
        }
      });
    }

    updateClearVisibility() {
      if (!this.elements.clearBtn) return;
      const hasQuery = this.elements.addressInput?.value.trim().length > 0;
      this.elements.clearBtn.classList.toggle('show', hasQuery);
      this.elements.clearBtn.setAttribute('aria-hidden', hasQuery ? 'false' : 'true');
    }

    clearSearch() {
      if (this.elements.addressInput) this.elements.addressInput.value = '';
      clearTimeout(this._searchDebounceTimer);
      this.cityFilter = null;
      this.updateClearVisibility();
      if (this.defaultCenter) this.userLocation = { ...this.defaultCenter };
    }

    // ── Nearest stores list ───────────────────────────────────────────────────

    async updateNearestStores() {
      if (!isValidLatLng(this.userLocation)) {
        console.warn('No valid user location available for nearby stores');
        this.showNoLocationsMessage();
        return;
      }

      if (this.elements.nearbyContainer.style.display === "none") {
        this.elements.nearbyContainer.style.display = "block";
      }

      if (this.elements.noResults) this.elements.noResults.classList.add('d-none');
      if (this.elements.loader) this.elements.loader.classList.remove('d-none');
      if (this.elements.locationFilters) this.elements.locationFilters.classList.add('d-none');

      this.stores.forEach((store) => {
        store.distance = store.getDistance(this.userLocation);
        store.distanceFromDevice = isValidLatLng(this.cachedGeolocation)
          ? store.getDistance(this.cachedGeolocation)
          : null;
      });

      const favoritedHandle = this.getFavoritedStoreHandle();
      let storesInRange;

      if (this.cityFilter) {
        const cityLower = this.cityFilter;
        const storesInCity = this.stores.filter(
          (s) => s.city && s.city.toLowerCase() === cityLower
        );
        if (storesInCity.length > 0) {
          storesInRange = storesInCity;
        } else {
          storesInRange = this.stores.filter(
            (s) => typeof s.distance === 'number' && typeof range === 'number' ? s.distance <= range : true
          );
        }
      } else {
        storesInRange = this.stores.filter(
          (s) => typeof s.distance === 'number' && typeof range === 'number' ? s.distance <= range : true
        );
      }

      const storesForList = [...storesInRange];

      if (storesInRange.length === 0) {
        const hadExistingList = this.listingsNearby.length > 0;
        this.showNoLocationsMessage(range, this.noNearbyStoresFound, { preserveList: hadExistingList });
        if (!hadExistingList) return;
        if (this.elements.loader) this.elements.loader.classList.add('d-none');
        return;
      }

      const sortedStores = this.sortByPriority([...storesForList], this.currentSortMode);

      if (this.map) {
        const bounds = new google.maps.LatLngBounds();
        storesInRange.forEach((s) => {
          if (isValidLatLng(s.location)) bounds.extend(new google.maps.LatLng(s.location.lat, s.location.lng));
        });
        if (isValidLatLng(this.userLocation)) {
          bounds.extend(new google.maps.LatLng(this.userLocation.lat, this.userLocation.lng));
        }
        if (!bounds.isEmpty()) this.map.fitBounds(bounds, 120);
      }

      this.clearNearestStores();

      this.listingsNearby = sortedStores.map((store, i) =>
        new Listing(store, { showDistance: true, label: i + 1 })
      );

      this.listingsNearby.forEach((listing) => {
        if (favoritedHandle && listing.store.handle === favoritedHandle) {
          listing.element.classList.add('is-active');
        }
        this.elements.nearestStoresList.appendChild(listing.element);
      });

      this.elements.loader.classList.add('d-none');
      this.elements.locationFilters.classList.remove('d-none');
      this.elements.staticLocations.classList.remove('loading');

      this.fetchWaitTimesForStores(storesForList).then(() => {
        this.updateDynamicCardWaitTimes();
        if (this.currentSortMode === "waitTime" && this.listingsNearby.length) {
          this.applySortFilter("waitTime");
        }
      });

      this.startDynamicWaitTimeRefresh();
      if (window.initializeCheckInLinks) window.initializeCheckInLinks();
    }

    clearNearestStores() {
      if (this.elements.nearestStoresList) this.elements.nearestStoresList.innerHTML = "";
      this.listingsNearby.forEach((l) => l.destroy());
      this.listingsNearby = [];
      this.stopDynamicWaitTimeRefresh();
    }

    showNoLocationsMessage(range, noMatchingStores, { preserveList = false } = {}) {
      if (this.elements.nearbyContainer.style.display === "none") {
        this.elements.nearbyContainer.style.display = "block";
      }
      if (!preserveList) this.clearNearestStores();

      this.elements.loader.classList.add('d-none');
      if (this.elements.locationFilters) this.elements.locationFilters.classList.remove('d-none');

      if (this.elements.noResults) {
        this.elements.noResults.classList.remove('d-none');
        if (this.elements.noResultsMessage) {
          this.elements.noResultsMessage.innerHTML = noMatchingStores
            ? `We couldn't find any barbershops near that location. Try finding a location near you in the list below.`
            : `We could not locate a barbershop near you. Try searching for a location nearby!`;
        }
      }
      this.elements.staticLocations.classList.remove('loading');
    }

    // ── Wait times ────────────────────────────────────────────────────────────

    async fetchWaitTimesForStores(stores) {
      if (!stores.length) return;

      const storeStatus = new StoreStatus();
      const storeIds = stores
        .filter((s) => storeEligibleForLiveWaitUi(s))
        .map((s) => s.shortcuts_id)
        .filter(Boolean);

      let waitTimeData = {};
      if (storeIds.length) waitTimeData = await storeStatus.fetchShopStatuses(storeIds);

      stores.forEach((store) => {
        if (!storeEligibleForLiveWaitUi(store)) {
          store.waitTimeData = null;
          store.waitTime = Infinity;
        } else {
          const data = waitTimeData[store.shortcuts_id];
          store.waitTimeData = data || null;
          store.waitTime = data?.shopStatus === "Open" ? (data.waitTime || 0) : Infinity;
        }
        store.updateMarkerSubtitle();
      });
    }

    updateDynamicCardWaitTimes() {
      this.listingsNearby.forEach((listing) => {
        if (!storeEligibleForLiveWaitUi(listing.store)) return;
        if (listing.element && listing.store.shortcuts_id) {
          document.dispatchEvent(new CustomEvent('dispatch:shop-data', {
            detail: { hours: false, waitTimes: true, wrapper: listing.element, filter: listing.store.shortcuts_id },
          }));
        }
      });
    }

    startDynamicWaitTimeRefresh() {
      if (dynamicWaitTimeInterval) clearInterval(dynamicWaitTimeInterval);
      dynamicWaitTimeInterval = setInterval(() => this.updateDynamicCardWaitTimes(), 60000);
    }

    stopDynamicWaitTimeRefresh() {
      if (dynamicWaitTimeInterval) { clearInterval(dynamicWaitTimeInterval); dynamicWaitTimeInterval = null; }
    }

    // ── Sorting ───────────────────────────────────────────────────────────────

    getFavoritedStoreHandle() {
      return localStorage.getItem('tg--webcheckin-fav') || null;
    }

    sortByPriority(stores, mode = this.currentSortMode) {
      const fav = this.getFavoritedStoreHandle();
      return stores.sort((a, b) => {
        if (a.handle === fav && b.handle !== fav) return -1;
        if (a.handle !== fav && b.handle === fav) return 1;
        if (mode === 'waitTime') {
          if (a.waitTime !== b.waitTime) return a.waitTime - b.waitTime;
        }
        return a.distance - b.distance;
      });
    }

    applySortFilter(mode) {
      this.currentSortMode = mode;
      if (!this.listingsNearby.length) return;

      const sorted = this.sortByPriority(
        [...this.listingsNearby.map((l) => l.store)],
        mode
      );

      this.listingsNearby = sorted
        .map((store) => this.listingsNearby.find((l) => l.store === store))
        .filter(Boolean);

      document.dispatchEvent(new CustomEvent('dispatch:flip:reorder-cards', {
        detail: {
          containerSelector: '.js-nearest-stores',
          stagger: true,
          newOrderElements: this.listingsNearby.map((l) => l.element).filter(Boolean),
        },
      }));
    }

    // ── Markers ───────────────────────────────────────────────────────────────

    setMarkers() {
      this.stores.forEach((store) => {
        store.addMarker(this.map);
        if (!store.marker) return;

        store.marker.addEventListener("gmp-click", () => {
          window.location.href = store.url;
        });
        store.marker.addEventListener("pointerenter", () => this.setEmphasizedMarkerZIndex(store));
        store.marker.addEventListener("pointerleave", (e) => {
          if (e.relatedTarget && store.marker.contains(e.relatedTarget)) return;
          this.resetAllMarkerZIndex();
        });
      });
    }

    resetAllMarkerZIndex() {
      this.stores.forEach((s) => { if (s.marker) s.marker.zIndex = MARKER_Z_DEFAULT; });
    }

    setEmphasizedMarkerZIndex(target) {
      this.stores.forEach((s) => {
        if (s.marker) s.marker.zIndex = s === target ? MARKER_Z_EMPHASIS : MARKER_Z_DEFAULT;
      });
    }

    focusStore = (store) => {
      [...this.listingsNearby, ...this.listings].forEach((listing) => {
        if (listing.element) {
          listing.element.classList.toggle("is-active", listing.store.shortcuts_id === store.shortcuts_id);
        }
      });

      if (this.map) {
        const target = new google.maps.LatLng(store.location.lat, store.location.lng);
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(this.map.getCenter());
        bounds.extend(target);
        this.map.fitBounds(bounds, 120);

        const listener = google.maps.event.addListenerOnce(this.map, "tilesloaded", () => {
          this.map.panTo(target);
        });
        setTimeout(() => google.maps.event.removeListener(listener), 2000);
      }
    };

    // ── Card ↔ Marker hover sync ──────────────────────────────────────────────

    clearCardMarkerHover({ resetZIndex = true } = {}) {
      this.elements.map?.classList.remove('is-card-hovering-gmp');
      this.stores.forEach((s) => { if (s.marker) s.marker.classList.remove('is-gmp-card-target'); });
      if (resetZIndex) this.resetAllMarkerZIndex();
    }

    setCardMarkerHoverFromCard(card) {
      this.clearCardMarkerHover();
      const sid = card.getAttribute('data-shortcut-id');
      if (!sid || !this.map) return;

      const store = this.stores.find((s) => String(s.shortcuts_id) === String(sid));
      if (!store?.marker || !isValidLatLng(store.location)) return;

      const bounds = this.map.getBounds();
      if (bounds && !bounds.contains(new google.maps.LatLng(store.location.lat, store.location.lng))) return;

      this.elements.map.classList.add('is-card-hovering-gmp');
      store.marker.classList.add('is-gmp-card-target');
      this.setEmphasizedMarkerZIndex(store);
    }

    // ── Static listings ───────────────────────────────────────────────────────

    initStaticListings() {
      document.querySelectorAll('.js-stores-list .location').forEach((elem) => {
        const store = this.stores.find((s) => s.shortcuts_id === elem.dataset.shortcutId);
        if (store) this.listings.push(new Listing(store, { element: elem }));
      });
    }

    // ── DOM events ────────────────────────────────────────────────────────────

    bindEvents() {
      if (this.elements.addressInput) this.initAutoSearch(this.elements.addressInput);

      this.elements.clearBtn?.addEventListener('click', () => this.clearSearch());

      this.elements.searchBtn?.addEventListener('click', () => {
        if (this.elements.addressInput && this._runSearch) {
          clearTimeout(this._searchDebounceTimer);
          this._runSearch(this.elements.addressInput.value);
        }
      });

      document.addEventListener('click', (e) => {
        const btn = e.target.closest('.btn--checkin');
        if (!btn) return;
        e.preventDefault();
        const target = document.getElementById('checkinButton');
        if (target) target.setAttribute('href', btn.dataset.checkinUrl);
      });

      const closestBtn = document.getElementById('closest-filter-button');
      const waitTimeBtn = document.getElementById('wait-time-filter-button');

      closestBtn?.addEventListener('click', () => {
        if (this.currentSortMode === 'distance') return;
        this.applySortFilter('distance');
        closestBtn.classList.add('active');
        waitTimeBtn?.classList.remove('active');
      });

      waitTimeBtn?.addEventListener('click', () => {
        if (this.currentSortMode === 'waitTime') return;
        this.applySortFilter('waitTime');
        waitTimeBtn.classList.add('active');
        closestBtn?.classList.remove('active');
      });

      const root = document.querySelector('.store-locator');
      if (root) {
        root.addEventListener('pointerover', (e) => {
          if (e.target.closest('gmp-advanced-marker')) {
            this._cardHoverForMarker = null;
            this.clearCardMarkerHover({ resetZIndex: false });
            return;
          }
          const card = e.target.closest('.location');
          if (!card || this._cardHoverForMarker === card) return;
          this._cardHoverForMarker = card;
          this.setCardMarkerHoverFromCard(card);
        });

        root.addEventListener('pointerout', (e) => {
          const card = e.target.closest('.location');
          if (!card) return;
          if (e.relatedTarget && card.contains(e.relatedTarget)) return;
          this._cardHoverForMarker = null;
          this.clearCardMarkerHover();
        });
      }
    }
  }

  // ── Bootstrap ─────────────────────────────────────────────────────────────────

  function initMap() {
    map = new StoreLocator(document.getElementById("map"));
    map.on('storesReady', () => map.initStaticListings());
  }

  function loadGoogleMaps() {
    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=${KEY}&loading=async&callback=initMap&v=weekly&libraries=marker,places`;
    script.async = true;
    window.initMap = initMap;
    document.head.appendChild(script);
  }

  function mapToggle() {
    const showMap = document.querySelector(".js-show-map");
    const mapElement = document.getElementById("map");
    let loaded = false;

    if (showMap && mapElement) {
      showMap.addEventListener("click", () => {
        if (!loaded && isDesktopMapLayout() && map && !map.loaded) {
          map.load();
          loaded = true;
        }
        mapElement.style.display = getComputedStyle(mapElement).display === "none" ? "block" : "none";
        showMap.classList.toggle("is-active");
      });
    }
  }

  window.addEventListener("load", () => {
    loadGoogleMaps();
    mapToggle();
  });
})();
